import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "@/state/useAccount";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/team",
)({
  component: TeamSettings,
  beforeLoad: () => ({
    title: "Team Settings",
    headerTitle: "Team Settings",
    headerDescription: "Manage your app's team members.",
  }),
});

export default function TeamSettings() {
  const { appId } = useAccount();
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const [email, setEmail] = useState("");
  const [selectedMember, setSelectedMember] = useState<{
    id: Id<"users">;
    name: string | undefined;
  } | null>(null);

  const { toast } = useToast();

  const { data: founders } = useQuery(
    convexQuery(api.invites.getAppFounders, { appId: appId as Id<"apps"> }),
  );

  const { data: invites } = useQuery(
    convexQuery(api.invites.getAppFounderInvites, { appId }),
  );

  const { mutateAsync: inviteFounder, isPending: isInviting } = useMutation({
    mutationFn: useConvexMutation(api.invites.inviteFounder),
  });

  const { mutateAsync: deleteInvite, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.invites.deleteTeamInvite),
  });

  const { mutateAsync: removeTeamMember, isPending: isRemoving } = useMutation({
    mutationFn: useConvexMutation(api.invites.removeTeamMember),
  });

  if (!appId) {
    return null;
  }

  const handleInvite = async () => {
    if (!email) return;
    try {
      await inviteFounder({ appId, email });
      setEmail("");
      toast({
        title: "Invite sent",
        description:
          "The invite has been sent to the email address. If they accept, they will be added to the team.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to invite the user. Please try again.",
      });
    }
  };

  const handleDeleteInvite = async (inviteId: Id<"appFounderInvites">) => {
    try {
      await deleteInvite({ inviteId });
      toast({
        title: "Invite deleted",
        description: "The invitation has been deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to delete the invite. Please try again.",
      });
    }
  };

  const handleRemoveTeamMember = async () => {
    if (!selectedMember || !appId) return;
    try {
      await removeTeamMember({ appId, teamUserId: selectedMember.id });
      setSelectedMember(null);
      toast({
        title: "Team member removed",
        description: "The team member has been removed successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to remove the team member. Please try again.",
      });
    }
  };

  const pendingInvites =
    invites?.filter((invite) => invite.status === "pending") || [];
  const rejectedInvites =
    invites?.filter((invite) => invite.status === "rejected") || [];

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Current Team Members */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">Team Members</h2>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {founders?.map((founder) => (
              <div
                key={founder._id}
                className="flex items-center justify-between rounded-lg border border-border p-4"
              >
                <div className="flex flex-col pr-8">
                  <span className="text-sm font-medium text-primary">
                    {founder.userName || "Unnamed"}
                  </span>
                  <span className="text-sm text-primary/60">
                    {founder.userEmail}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-primary/60">Admin</span>
                  {founder.userId !== user?._id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() =>
                        setSelectedMember({
                          id: founder.userId,
                          name: founder.userName,
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Invite New Member */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6 w-full">
          <h2 className="text-xl font-medium text-primary">
            Invite Team Member
          </h2>
          <p className="text-sm font-normal text-primary/60">
            Invite a new team member to join your app.
          </p>
          <div className="mt-4 flex w-full gap-2">
            <Input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full"
            />
            <Button onClick={handleInvite} disabled={isInviting || !email}>
              {isInviting ? "Inviting..." : "Send Invite"}
            </Button>
          </div>
        </div>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6">
            <h2 className="text-xl font-medium text-primary">
              Pending Invites
            </h2>
            <div className="mt-4 space-y-2">
              {pendingInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="flex items-center justify-between rounded-lg border border-border p-4"
                >
                  <div className="flex flex-col">
                    <span className="text-sm text-primary/60 pr-8">
                      {invite.inviteeEmail}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-yellow-500">Pending</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() => handleDeleteInvite(invite._id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rejected Invites */}
      {rejectedInvites.length > 0 && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6">
            <h2 className="text-xl font-medium text-primary">
              Rejected Invites
            </h2>
            <div className="mt-4 space-y-2">
              {rejectedInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="flex items-center justify-between rounded-lg border border-border p-4"
                >
                  <div className="flex flex-col">
                    <span className="text-sm text-primary/60 pr-8">
                      {invite.inviteeEmail}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-red-500">Rejected</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() => handleDeleteInvite(invite._id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Remove Team Member Confirmation Dialog */}
      <Dialog
        open={!!selectedMember}
        onOpenChange={(open) => !open && setSelectedMember(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              {selectedMember?.name || "this team member"}? They will lose
              access to all existing partnerships and app data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedMember(null)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveTeamMember}
              disabled={isRemoving}
            >
              {isRemoving ? "Removing..." : "Remove Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
